import { useCallback } from 'react';
import { useDispatch, useRouter, useServices } from 'core/common/hooks';
import { appendQueryParams } from 'core/common/utils/appendQueryParams';
import { getQueryParams, stringifyQueryParams } from 'core/common/utils/getQueryParams';
import { isAbsolutePath } from 'core/common/utils/isAbsolutePath';
import { joinUrls } from 'core/common/utils/joinUrls';
import { redirectToUrl } from 'core/common/utils/redirectToUrl';
import {
  BasePageAttributesWithMappedSections,
  BaseScreenAttributes,
  ScreenType,
} from 'core/flow/entities';
import { isUtmTag } from 'core/user/entities';
import { useUser } from 'core/user/hooks';

type Params = {
  rootFunnelLink: string;
  flowInitialScreenId: string;
  getScreenAttributes: (
    pageId: string,
  ) => BaseScreenAttributes | BasePageAttributesWithMappedSections;
};

export const useFlowNavigation = ({
  rootFunnelLink,
  flowInitialScreenId,
  getScreenAttributes,
}: Params) => {
  const dispatch = useDispatch();
  const { navigate } = useRouter();
  const { userPreferences, updateUserPreferences } = useUser();
  const { analyticsService, appEnvironment } = useServices();

  const isTesting = appEnvironment.isTestingEnv();

  const navigateTo = useCallback(
    (step: string) => {
      const queryParams = getQueryParams();

      if (isAbsolutePath(step)) {
        return redirectToUrl(appendQueryParams(step, queryParams));
      }

      // Persist chosen experiment group on navigation (only in testing mode)
      if (isTesting && Object.keys(queryParams).length) {
        const filteredQsEntries = Object.entries(queryParams).filter(([key]) => !isUtmTag(key));
        const qsWithoutUtmTags = Object.fromEntries(filteredQsEntries);

        navigate(joinUrls(rootFunnelLink, step, `?${stringifyQueryParams(qsWithoutUtmTags)}`));

        return;
      }

      navigate(joinUrls(rootFunnelLink, step));
    },
    [navigate, rootFunnelLink, isTesting],
  );

  const goNext = useCallback(
    async ({ id, value }: { id: string; value?: string | string[] }) => {
      if (id === flowInitialScreenId || 'unknown') {
        updateUserPreferences({ initialStep: flowInitialScreenId });
      }

      if (value) {
        trackAnalyticUserChoice(id, value);
      }

      const nextScreen = getNextScreen(id);

      navigateTo(nextScreen);

      return nextScreen;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, flowInitialScreenId, navigateTo, userPreferences.initialStep],
  );

  const getExtraNextScreen = useCallback(
    (id: string) => {
      const { extraNext } = getScreenAttributes(id);

      if (!extraNext) return '';

      if (getScreenAttributes(extraNext).screenType !== ScreenType.SPLIT) return extraNext;

      return getScreenAttributes(id).extraNext;
    },
    [getScreenAttributes],
  );

  const getPreviousScreen = useCallback(
    (id: string) => {
      const { prev } = getScreenAttributes(id);

      if (!prev) return '';

      if (getScreenAttributes(prev).screenType !== ScreenType.SPLIT) return prev;

      return getScreenAttributes(prev).next;
    },
    [getScreenAttributes],
  );

  const goExtraNext = useCallback(
    (id: string) => {
      const extraNextScreen = getExtraNextScreen(id);

      if (!extraNextScreen) return;

      navigateTo(extraNextScreen);
    },
    [getExtraNextScreen, navigateTo],
  );

  const goBack = useCallback(
    (id: string) => {
      const prevScreen = getPreviousScreen(id);

      if (!prevScreen) return;

      navigateTo(prevScreen);
    },
    [getPreviousScreen, navigateTo],
  );

  const getNextScreen = (id: string) => {
    const { next } = getScreenAttributes(id);

    if (!next) return '';

    if (getScreenAttributes(next).screenType !== ScreenType.SPLIT) return next;

    return getScreenAttributes(next).next;
  };

  // TODO: remove when FB events will be updated
  const trackAnalyticUserChoice = (id: string, text: string | string[]) => {
    const { analytic } = getScreenAttributes(id);

    if (!analytic?.clickEvent || typeof analytic.clickEvent !== 'string') return;

    analyticsService.trackEvent(analytic.clickEvent, {
      context: text,
    });
  };

  return { goNext, goExtraNext, goBack };
};
